"use client";

import { useRef, useEffect, useLayoutEffect, useState } from "react";
import SkeletonImage from "@/components/ui/SkeletonImage";
import ProfileLogoBadge, { LOGO_REVEAL_CLASS } from "@/components/ui/ProfileLogoBadge";
import Link from "@/components/ui/LocaleLink";
import { useTranslations } from "next-intl";
import { MapPin, Globe, Briefcase } from "@phosphor-icons/react/dist/ssr";
import VerifiedIcon from "@/components/ui/VerifiedIcon";
import { showsVerifiedBadge } from "@/lib/listing-visibility";
import FounderIcon from "@/components/ui/FounderIcon";
import { toLangTokens } from "@/lib/lang-utils";
import { BADGE_TAB_CATEGORY } from "@/lib/badge-styles";
import { CARD_LANG_CODES_SHOWN, GRID_CARD_FOOTER, GRID_CARD_NAME_LEADING, GRID_CARD_TAB, PHOTO_HOVER_ZOOM, gridCardNameSize } from "@/lib/card-styles";
import { getCategoryLabel, type DirectoryCategory } from "@/lib/categories";
import { type LocationSlug } from "@/lib/provinces";
import { formatLocationShort } from "@/lib/location-short";
import { useLocale } from "@/lib/hooks/use-locale";
import type { Locale } from "@/lib/locale";
import type { ImageType } from "@/lib/image-utils";

export interface Professional {
  slug: string;
  name: string;        // personal full name (internal/admin use)
  displayName?: string | null; // business/display name (public facing, falls back to name)
  title: string;
  category: string;
  /** Pre-formatted English display string (legacy — prefer `languageCodes`). */
  languages: string;
  /** Raw ISO codes — preferred for locale-aware rendering. */
  languageCodes?: string[];
  /** Canonical slug of the MAIN office — empty string when unset. Render via formatLocation(). */
  location: LocationSlug;
  /**
   * Every province the pro has an office in (multi-office). Drives the
   * "+N" suffix after the main city. Optional — consumers not yet migrated
   * (or single-office pros) render exactly as before.
   */
  provinceSlugs?: string[];
  verified: boolean;
  /** Pricing tier. Public badge display is `verified && paid` — always ask via
   *  `showsVerifiedBadge`, never `verified` alone, on a public surface. */
  plan?: string | null;
  /** Founding member — renders a gold founder badge before the verified badge. */
  founder: boolean;
  image: string;
  /** "photo" = cover-cropped headshot; "logo" = company logo shown contained in a navy/gold circle. */
  imageType: ImageType;
  yearsExperience?: number;
  secondaryCategories?: string[];
}

// useLayoutEffect on the client (measure before paint = no flash), useEffect on
// the server (it's a no-op there anyway) — avoids React's SSR warning.
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// SSR/no-JS FALLBACK size for the name: `gridCardNameSize` in card-styles.ts
// (shared with the affiliate grid card, so the two stay in one row's rhythm).

// Largest font (px) we'll grow a name to, and the readable floor, per card mode.
const NAME_FIT = { full: { max: 20, min: 11 }, compact: { max: 16, min: 11 } } as const;

export default function ProfessionalCard({ pro, locale: initialLocale = "en", compact = false }: { pro: Professional; locale?: Locale; compact?: boolean }) {
  // Hook seeds with the server-provided locale so the first hydration render
  // matches SSR (no flash), then flips live on EN/VI toggle without refresh.
  const locale = useLocale(initialLocale);
  const t = useTranslations("a11y");
  const displayName = pro.displayName ?? pro.name;

  // Fit the name to the card: measure the actual available width and use the
  // largest font (≤ max) that keeps it on one line, floored at a readable min.
  // Refits on card/viewport resize. Fills wide cards (no wasted space) and
  // shrinks on narrow ones — what the char-count fallback above can't do since
  // it doesn't know the rendered width.
  const nameRef = useRef<HTMLSpanElement>(null);
  useIsoLayoutEffect(() => {
    const el = nameRef.current;
    const body = el?.closest("h3")?.parentElement;
    if (!el || !body) return;
    const { max, min } = compact ? NAME_FIT.compact : NAME_FIT.full;
    let lastWidth = -1;
    const fit = () => {
      const w = body.clientWidth;
      if (w === lastWidth) return; // width unchanged → skip (avoids RO height-loop)
      lastWidth = w;
      el.style.fontSize = `${max}px`;
      if (el.scrollWidth <= el.clientWidth) return; // fits at max — done
      // Linear first guess, then step down measuring at each size (Playfair's
      // width isn't perfectly linear at small px, so a single estimate can clip).
      let size = Math.max(min, Math.floor((max * el.clientWidth) / el.scrollWidth));
      el.style.fontSize = `${size}px`;
      while (size > min && el.scrollWidth > el.clientWidth) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(body);
    return () => ro.disconnect();
  }, [displayName, compact]);

  // Prefer locale-aware codes when available; fall back to the pre-formatted
  // English string for any consumer that hasn't been migrated yet. Build the
  // aggregate aria/title from the SAME localized tokens as the inline chips:
  // toLangTokens localizes the legacy English string too, so the tooltip flips
  // to VI even when `languageCodes` isn't populated (e.g. the homepage featured
  // query). Don't read raw `pro.languages` here or the aggregate tooltip
  // silently stays English.
  const langTokens = !compact && pro.languages ? toLangTokens(pro.languageCodes ?? pro.languages, locale) : [];
  const langKey = langTokens.map((tk) => tk.code).join(",");

  // Fit the language codes to the meta row, like the name above: as many as
  // fit, the rest folded into "+N". The width left for them depends on the
  // card (192px at lg and xl, wider below) AND on the location beside them
  // (only Ho Chi Minh has a short label), so no fixed count fits every card.
  // Widths come from a hidden copy of the full row (`langMeasureRef`); the
  // server renders CARD_LANG_CODES_SHOWN until this runs.
  const footerRef = useRef<HTMLElement>(null);
  const langMeasureRef = useRef<HTMLSpanElement>(null);
  const [langFit, setLangFit] = useState(CARD_LANG_CODES_SHOWN);
  useIsoLayoutEffect(() => {
    const footer = footerRef.current;
    const measure = langMeasureRef.current;
    if (!footer || !measure) return;
    let lastWidth = -1;
    const fit = () => {
      const w = footer.clientWidth;
      if (w === lastWidth) return;
      lastWidth = w;
      const firstChip = footer.querySelector<HTMLElement>("[data-lang-chip]");
      const codes = [...measure.querySelectorAll<HTMLElement>("[data-lang-code]")];
      const more = measure.querySelector<HTMLElement>("[data-lang-more]");
      if (!firstChip || codes.length === 0 || !more) return;
      const fs = getComputedStyle(footer);
      const gap = parseFloat(fs.columnGap) || 0;
      // The row ends at the content edge, less the years item when there is one.
      const years = footer.querySelector<HTMLElement>("[data-meta-years]");
      const end = footer.getBoundingClientRect().right - parseFloat(fs.paddingRight)
        - (years ? years.getBoundingClientRect().width + gap : 0);
      const room = end - firstChip.getBoundingClientRect().left;
      const origin = codes[0].getBoundingClientRect().left;
      const moreGap = parseFloat(getComputedStyle(measure).columnGap) || 0;
      const moreW = more.getBoundingClientRect().width;
      let n = codes.length;
      if (codes[n - 1].getBoundingClientRect().right - origin > room) {
        n -= 1;
        while (n > 1 && codes[n - 1].getBoundingClientRect().right - origin + moreGap + moreW > room) n -= 1;
      }
      setLangFit(n);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(footer);
    // A web font swap changes every width without resizing the footer.
    let live = true;
    document.fonts?.ready.then(() => { if (live) { lastWidth = -1; fit(); } });
    return () => { live = false; ro.disconnect(); };
  }, [langKey, locale]);
  return (
    // ⭐ THE META ROW SITS ON THE CARD'S BOTTOM EDGE, not under whatever the
    // title happened to need (owner, 2026-09-18): a two-line title pushed its
    // own footer down and the ruled lines stopped agreeing across the row.
    // A grid item already stretches to the tallest card in its row, so the
    // card only has to FILL that height (`h-full`) and grow the block above
    // the footer (`flex-1` below). ⛔ Not `mt-auto` on the footer: its recipe
    // already sets `mt-13`, and two margin-top utilities on one element leave
    // the winner to stylesheet order.
    <article aria-label={`${pro.displayName ?? pro.name}, ${pro.title}`} className="h-full">
      <Link
        href={`/professionals/${pro.slug}`}
        className="group flex h-full flex-col focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2"
        tabIndex={0}
      >
        {/* Image — inset top/sides to align with card body padding */}
        <div className={compact ? "px-13 pt-13" : "px-21 pt-21"}>
          {/* Outer box = positioning context, does NOT clip; inner box clips.
              The category tab hangs out to the card's edge and would be cut
              off at the photo's edge if it lived inside the clipping well. */}
          <div className="relative">
            <div className="overflow-hidden rounded-control">
            {pro.image && pro.imageType === "logo" ? (
              <ProfileLogoBadge
                src={pro.image}
                alt={`${pro.displayName ?? pro.name} logo`}
                sizes="(min-width: 1024px) 360px, (min-width: 640px) 50vw, 90vw"
                className="aspect-[3/4] w-full"
                imageClassName={LOGO_REVEAL_CLASS}
              />
            ) : pro.image ? (
              <SkeletonImage
                src={pro.image}
                alt={`${pro.displayName ?? pro.name}, ${pro.title}`}
                width={400}
                height={300}
                className={`aspect-[3/4] w-full object-cover ${PHOTO_HOVER_ZOOM}`}
              />
            ) : (
              <div className="aspect-[3/4] w-full bg-navy/5" aria-hidden="true" />
            )}
            </div>
            {/* Flush to the CARD's left edge — the negative inset IS the card's
                own padding, so it differs between the two densities. Capped at
                70% across the photo; a longer label wraps instead of reaching
                over the subject. */}
            <span
              className={`${BADGE_TAB_CATEGORY} ${compact ? GRID_CARD_TAB.compact : GRID_CARD_TAB.full}`}
            >
              {getCategoryLabel(pro.category as DirectoryCategory, locale) ?? pro.category}
            </span>
          </div>
        </div>

        {/* Body */}
        {/* `rounded-b-card` is LOAD-BEARING, not decoration. This body is
            opaque white and sits flush against the card's bottom edge, and
            the card is `overflow: visible` (it has to be — an inner Link
            draws a `focus-visible` ring with `outline-offset-2` that
            `overflow-hidden` would clip). A SQUARE opaque child therefore
            paints straight over the parent's rounded bottom corners and the
            border vanishes there. Zero gap ⇒ outer radius = inner radius. */}
        <div className={`flex flex-1 flex-col ${compact ? "rounded-b-card bg-white p-13" : "rounded-b-card bg-white p-21"}`}>
          <div className="flex-1">
          <h3 className="flex items-center gap-8 font-serif text-navy">
            {/* min-w-0 lets this flex child shrink so `truncate` engages; the
                badges keep intrinsic size and stay put beside a long name. */}
            {/* Fixed line-height (independent of the fitted font-size) so the
                name row is the same height on every card — keeps the title/meta
                below aligned across a grid even when a long name renders smaller. */}
            <span ref={nameRef} className={`min-w-0 truncate ${compact ? GRID_CARD_NAME_LEADING.compact : GRID_CARD_NAME_LEADING.full} ${gridCardNameSize(displayName.length, compact)}`}>{displayName}</span>
            {pro.founder && <FounderIcon size={compact ? 14 : 16} nudge={1} />}
            {showsVerifiedBadge(pro) && <VerifiedIcon size={compact ? 14 : 16} nudge={1} />}
          </h3>
          <p className={`mt-2 text-gray-600 ${compact ? "truncate text-xs" : "text-sm"}`}>{pro.title}</p>
          </div>

          {/* Meta */}
          <footer
            ref={footerRef}
            className={`${GRID_CARD_FOOTER} relative`}
            aria-label={t("cardMeta")}
          >
            <span className="flex shrink-0 items-center gap-5">
              <MapPin aria-hidden="true" weight="bold" className="w-13 h-13 shrink-0" />
              {formatLocationShort(pro.location, locale)}
              {/* Multi-office: "+N" = provinces beyond the main city. Two
                  offices in the SAME province deliberately show nothing —
                  the label is about WHERE, not how many rooms. The meta row
                  is overflow-hidden and shared with languages/years, so the
                  main short label + "+N" is all that fits (never a list). */}
              {(() => {
                const extra = (pro.provinceSlugs ?? []).filter((s) => s && s !== pro.location).length;
                return extra > 0 ? (
                  <>
                    <span aria-hidden="true">+{extra}</span>
                    <span className="sr-only">{t("plusNLocations", { n: extra })}</span>
                  </>
                ) : null;
              })()}
            </span>
            {langTokens.length > 0 && (() => {
              const ariaLabel = langTokens.map((tk) => tk.label).join(", ");
              // Codes past the fit fold into "+N", whose tooltip lists them one per
              // line, so a long list grows down the card instead of off its side.
              const shown = langTokens.slice(0, langFit);
              const folded = langTokens.slice(langFit);
              const chips = folded.length > 0
                ? [...shown, { code: `+${folded.length}`, label: folded.map((tk) => tk.label).join("\n") }]
                : shown;
              return (
              <span className="flex min-w-0 items-center gap-5 whitespace-nowrap" aria-label={ariaLabel} title={ariaLabel}>
                <Globe aria-hidden="true" weight="bold" className="w-13 h-13 shrink-0" />
                {chips.map(({ code, label }, i) => {
                  // A tooltip centred on the first or last code would hang
                  // past the card's edge: those two line up with that edge
                  // instead, and the arrow sits on the code itself so it points
                  // at it whichever way.
                  const align = chips.length > 1 && i === 0 ? "left-0" : i === chips.length - 1 && chips.length > 1 ? "right-0" : "left-1/2 -translate-x-1/2";
                  const reveal = "opacity-0 transition-opacity duration-150 group-hover/lang:opacity-100 group-focus-within/lang:opacity-100";
                  return (
                  <span key={code + i} data-lang-chip="" className="flex items-center gap-5">
                    <span className="group/lang relative" tabIndex={0}>
                      {code}
                      <span
                        role="tooltip"
                        className={`pointer-events-none absolute bottom-full ${align} mb-5 whitespace-pre rounded-control bg-navy px-[7px] py-3 font-sans text-[9px] font-semibold uppercase tracking-widest text-white shadow-e3 ${reveal}`}
                      >
                        {label}
                      </span>
                      <span aria-hidden="true" className={`pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 border-[3px] border-transparent border-t-navy ${reveal}`} />
                    </span>
                    {/* No slash before "+N": it reads like the location's "HCMC +1". */}
                    {i < shown.length - 1 && <span aria-hidden="true">/</span>}
                  </span>
                  );
                })}
              </span>
              );
            })()}
            {/* The full row, invisible, for the fit above to measure. Same
                spacing as the chips (code, slash and next chip are all gap-5
                apart); "+N" carries the most digits it can show. The empty
                clipping box keeps it from widening anything: the footer itself
                no longer clips. */}
            {langTokens.length > 1 && (
              <span aria-hidden="true" className="pointer-events-none invisible absolute left-0 top-0 size-0 overflow-hidden">
              <span ref={langMeasureRef} className="flex w-max items-center gap-5 whitespace-nowrap">
                {langTokens.map(({ code }, i) => (
                  <span key={code + i} className="flex items-center gap-5">
                    <span data-lang-code="">{code}</span>
                    {i < langTokens.length - 1 && <span>/</span>}
                  </span>
                ))}
                <span data-lang-more="">+{langTokens.length - 1}</span>
              </span>
              </span>
            )}
            {!compact && pro.yearsExperience != null && (
              <span data-meta-years="" className="flex shrink-0 items-center gap-5">
                <Briefcase aria-hidden="true" weight="bold" className="w-13 h-13 shrink-0" />
                {pro.yearsExperience}+ yrs
              </span>
            )}
          </footer>

          <span className="sr-only">{t("viewFullProfileOf", { name: pro.displayName ?? pro.name })}</span>
        </div>
      </Link>
    </article>
  );
}
